"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useState, type ReactNode } from "react"
import { Cpu, User, FileText, LayoutDashboard } from "lucide-react"
import { cn } from "@/lib/utils"
import { AutotradeHeaderSummary } from "@/components/dashboard/autotrade-header-summary"
import { SessionBadge } from "@/components/dashboard/session-badge"
import type { FxSessionContext } from "@/lib/agent-force"

const navItems = [
  { href: "/", label: "Dashboard", icon: LayoutDashboard },
  { href: "/articles", label: "Articles", icon: FileText },
]

const AGENT_MANAGER = {
  name: "Tony Mudau",
  href: "https://www.linkedin.com/in/tony-mudau-5148b018a/",
}

const MOBILE_TICKER_SCROLL =
  "overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"

interface DashboardHeaderProps {
  isApiDown?: boolean
  sessionContext?: FxSessionContext | null
}

function MobileTickerLink({
  href,
  external,
  active,
  children,
  className,
}: {
  href: string
  external?: boolean
  active?: boolean
  children: ReactNode
  className?: string
}) {
  const cls = cn(
    "inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2 py-1 text-[10px] font-medium whitespace-nowrap transition-colors",
    active
      ? "bg-secondary text-foreground"
      : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
    className,
  )
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {children}
      </a>
    )
  }
  return (
    <Link href={href} className={cls}>
      {children}
    </Link>
  )
}

export function DashboardHeader({ isApiDown = false, sessionContext = null }: DashboardHeaderProps) {
  const pathname = usePathname()

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="px-4 sm:px-6">
        {/* Row 1: brand + live (mobile) / full desktop bar */}
        <div className="flex h-14 min-w-0 items-center justify-between gap-3 sm:h-16 sm:gap-4">
          <div className="min-w-0 flex-1 overflow-hidden sm:overflow-x-auto sm:overflow-y-hidden sm:overscroll-x-contain sm:[-ms-overflow-style:none] sm:[scrollbar-width:none] sm:[&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-0 items-center gap-3 sm:w-max sm:gap-6">
              <Link href="/" className="flex shrink-0 items-center gap-2.5 sm:gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary">
                  <Cpu className="h-5 w-5 text-primary-foreground" />
                </div>
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-semibold tracking-tight leading-none">A.E.T Portfolio</span>
                  <span className="hidden text-xs text-muted-foreground sm:inline">agentic-edge-trading</span>
                </div>
              </Link>

              <div className="flex shrink-0 items-center gap-1.5">
                <span
                  className={cn(
                    "flex h-2 w-2 rounded-full",
                    isApiDown ? "bg-destructive" : "bg-positive animate-pulse",
                  )}
                />
                <span className="text-xs text-muted-foreground">{isApiDown ? "Down" : "Live"}</span>
              </div>

              <MobileTickerLink href={AGENT_MANAGER.href} external className="ml-auto shrink-0 sm:hidden">
                <User className="h-3 w-3 shrink-0" />
                {AGENT_MANAGER.name}
              </MobileTickerLink>

              <nav className="hidden shrink-0 items-center gap-1 sm:flex">
                {navItems.map((item) => {
                  const isActive = pathname === item.href
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={cn(
                        "flex items-center gap-2 whitespace-nowrap px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                        isActive
                          ? "bg-secondary text-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50",
                      )}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {item.label}
                    </Link>
                  )
                })}
              </nav>
            </div>
          </div>

          {/* Desktop-only utilities (sm+) */}
          <div className="hidden shrink-0 items-center gap-2 sm:flex sm:gap-4">
            <AutotradeHeaderSummary />
            <SessionBadge session={sessionContext} />
            <div className="hidden items-center gap-6 text-sm lg:flex">
              <Link
                href={AGENT_MANAGER.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-md border border-border bg-secondary/50 px-3 py-1.5 transition-colors hover:bg-secondary"
              >
                <User className="h-4 w-4 text-primary" />
                <div className="flex flex-col">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Agent Manager</span>
                  <span className="text-sm font-medium leading-none">{AGENT_MANAGER.name}</span>
                </div>
              </Link>

              <div className="h-8 w-px bg-border" />

              <div className="flex flex-col items-end">
                <span className="text-xs text-muted-foreground">Market Status</span>
                <span className="font-mono text-positive">OPEN</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-xs text-muted-foreground">UTC Time</span>
                <LiveClock />
              </div>
            </div>
          </div>
        </div>

        {/* Row 2 (mobile): horizontal ticker for status + quick links */}
        <div className={cn("border-t border-border/60 sm:hidden", MOBILE_TICKER_SCROLL)}>
          <div className="flex w-max min-w-full items-center gap-2 py-2 pr-4">
            <AutotradeHeaderSummary layout="inline" />
            <span className="text-muted-foreground/40 shrink-0" aria-hidden>
              |
            </span>
            <SessionBadge session={sessionContext} />
            <MobileTickerLink href="/articles" active={pathname === "/articles"}>
              <FileText className="h-3 w-3 shrink-0" />
              Articles
            </MobileTickerLink>
          </div>
        </div>
      </div>
    </header>
  )
}

function LiveClock() {
  const [time, setTime] = useState("--:--:--")

  useEffect(() => {
    const formatUtcTime = () =>
      new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "UTC",
      })

    setTime(formatUtcTime())
    const intervalId = setInterval(() => {
      setTime(formatUtcTime())
    }, 1000)

    return () => clearInterval(intervalId)
  }, [])

  return <span className="font-mono tabular-nums">{time}</span>
}
